import clsx from 'clsx';
import getLocale from '../../hooks/getLocale';
import {SanitySBXHero} from '../../types';
import SanityImage from '../media/SanityImage.client';
import sanityConfig from '../../../sanity.config';
import {useEffect, useState} from 'react';

type Props = {
  hero: SanitySBXHero;
};

export default function HeroSBXVideo({hero}: Props) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' && window.innerWidth < 1024,
  );

  useEffect(() => {
    function handleResize() {
      setIsMobile(window.innerWidth < 1024);
    }

    if (typeof window !== 'undefined') handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isMobile]);

  return (
    <div
      className={clsx(
        'sbx-video',
        'pt-header relative overflow-hidden py-[70px] xl:pb-[250px]',
      )}
    >
      {(hero?.desktop || hero?.mobile) && (
        <video
          key={hero._key}
          autoPlay
          muted
          playsInline
          loop
          className={clsx(
            'relative -top-[25px] right-0 z-10 h-full max-h-[1500px] min-h-[500px] w-full object-cover object-top', //
            'sm:min-h-[1000px]',
            'lg:absolute lg:top-[70px] lg:h-[calc(70vh-70px)]',
            'xl:max-w-[60%] xl:object-top',
            '2xl:h-[calc(90vh-70px)] 2xl:w-[75%] 2xl:object-top',
          )}
        >
          {hero.desktop && !isMobile && (
            <source src={hero.desktop} type="video/mp4" />
          )}
          {hero.mobile && isMobile && (
            <source src={hero.mobile} type="video/mp4" />
          )}
        </video>
      )}
      <div
        className={clsx(
          'relative mx-auto flex grid w-full grid-cols-1 flex-col items-end',
          'px-4 sm:px-12 lg:h-[calc(90vh-70px)] lg:grid-cols-2 lg:gap-16',
        )}
      >
        {hero?.poster && (
          <picture
            className={clsx(
              'relative left-0 right-0 top-0', //
              'lg:absolute',
            )}
          >
            <SanityImage
              className={'h-full'}
              crop={hero.poster.crop}
              dataset={sanityConfig.dataset}
              hotspot={hero.poster?.hotspot}
              layout="responsive"
              projectId={sanityConfig.projectId}
              src={hero.poster?.asset._ref}
              sizes="100vw"
            />
          </picture>
        )}
        <div
          className={clsx(
            'md:py-16 lg:max-w-[768px] 2xl:mx-auto',
            'text-blue lg:z-10 lg:bg-transparent',
          )}
        >
          {/* Title */}
          {hero?.title && (
            <h1
              className={clsx(
                'h1 mb-7 flex h-full max-w-[60rem] whitespace-pre-line text-left font-industry font-bold lg:flex-col',
                'lg:text-5xl',
              )}
            >
              {typeof hero.title === 'object'
                ? getLocale(hero.title)
                : hero.title}
            </h1>
          )}
          {/* Text */}
          {hero?.text && (
            <p className={clsx('p')}>
              {typeof hero.text === 'object' ? getLocale(hero.text) : hero.text}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
